/**
 * Admin sidebar navigation — the ONE place the sidebar, header title, section
 * tab strips and breadcrumbs take their structure and vocabulary from. Icons are
 * Lucide (the brand set). Refine's resources[].meta does NOT drive the sidebar.
 *
 * Rule: every admin screen is reachable by clicking — as a sidebar item or as a
 * tab of one (docs/admin-ia-revamp-plan.md). The single deliberate exception is
 * /mentorship_relationships, whose table is already the Mentorship hub's
 * Relationships tab. event_sessions IS in the nav (Events › Sub-events) — the
 * agenda inside each event is first-class, not a hidden extra.
 */
import {
  LayoutDashboard,
  BarChart3,
  UsersRound,
  Rocket,
  CalendarDays,
  CalendarCheck,
  HandHeart,
  Target,
  Trophy,
  Newspaper,
  Activity,
  FileText,
  Sunrise,
  Image as ImageIcon,
  MessagesSquare,
  Bell,
  ShieldCheck,
  ShieldAlert,
  Settings,
  Flag,
  Ban,
  RefreshCw,
  Send,
  History,
  Medal,
  GraduationCap,
  Trash2,
  Sparkles,
  UserCheck,
  Store,
  SlidersHorizontal,
  Ticket,
  ListFilter,
  type AdminIcon,
} from './icons';

/**
 * One tab of a sidebar item's section strip. A tab is an EXISTING route — the
 * strip only links between them, so URLs, deep links and history are untouched
 * (docs/admin-ia-revamp-plan.md §4).
 */
export interface AdminNavTab {
  to: string;
  label: string;
}
export interface AdminNavItem {
  /** Where the sidebar link lands — the first tab's route when `tabs` is set. */
  to: string;
  label: string;
  icon: AdminIcon;
  /** Two or more routes this one sidebar entry covers, shown as a tab strip. */
  tabs?: AdminNavTab[];
}
export interface AdminNavGroup {
  group: string;
  items: AdminNavItem[];
}

export const ADMIN_NAV: AdminNavGroup[] = [
  {
    group: 'Overview',
    items: [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard },
      { to: '/activity_feed', label: 'Activity', icon: History },
      {
        to: '/analytics/screens',
        label: 'Analytics',
        icon: BarChart3,
        tabs: [
          { to: '/analytics/screens', label: 'Screens' },
          { to: '/analytics/flow', label: 'User Flow' },
        ],
      },
      { to: '/sentry', label: 'App Health', icon: Activity },
    ],
  },
  {
    group: 'Members',
    items: [
      { to: '/profiles', label: 'Alumni', icon: UsersRound },
      // A sign-up roster (confirmation, last seen, bans) that is also where an
      // admin is promoted or revoked — not a staff directory.
      { to: '/admin_users', label: 'Accounts & Admins', icon: ShieldCheck },
      { to: '/class_year_change_requests', label: 'Class Year Changes', icon: GraduationCap },
      { to: '/user_settings', label: 'Member Settings', icon: Settings },
      { to: '/member_badges', label: 'Badges', icon: Medal },
    ],
  },
  {
    group: 'Community',
    items: [
      {
        to: '/mentorship_requests',
        label: 'Mentorship',
        icon: HandHeart,
        tabs: [
          { to: '/mentorship_requests', label: 'Hub' },
          { to: '/mentorships', label: 'Mentor Profiles' },
        ],
      },
      { to: '/jobs', label: 'Jobs', icon: Rocket },
      // Named for what members tap in the app. Never 'Ideas' — Groups owns that
      // tab label, and labels never repeat.
      { to: '/suggestions', label: 'Suggestion Box', icon: Sparkles },
      {
        to: '/groups',
        label: 'Groups',
        icon: UsersRound,
        tabs: [
          { to: '/groups', label: 'Groups' },
          // Named for what members see (Classrooms Phase 1). The route is unchanged.
          { to: '/group_chapters', label: 'Classrooms' },
          { to: '/group_proposals', label: 'Proposals' },
          { to: '/group_ideas', label: 'Ideas' },
          { to: '/group_meetups', label: 'Meetups' },
          { to: '/group_calls', label: 'Calls' },
          // P6 "Prove" (docs/groups/GROUPS-SPEC.md §8) — adoption dashboard.
          { to: '/group_insights', label: 'Insights' },
          { to: '/groups_settings', label: 'Group Settings' },
        ],
      },
      {
        to: '/class-chats',
        label: 'Chats',
        icon: MessagesSquare,
        tabs: [
          { to: '/class-chats', label: 'Class Chats' },
          { to: '/community', label: 'Reunion Chats' },
        ],
      },
      {
        to: '/businesses',
        label: 'Jaguar-Owned',
        icon: Store,
        tabs: [
          { to: '/businesses', label: 'Listings' },
          { to: '/business-of-the-month', label: 'Business of the Month' },
          { to: '/ask-monitor', label: 'Ask the Jaguars' },
          // The hub's per-part switches. 'Switches', not 'Controls', because
          // Community Manager owns the 'Controls' tab — labels never repeat.
          { to: '/jaguar-owned/controls', label: 'Switches' },
        ],
      },
    ],
  },
  {
    group: 'Events',
    items: [
      { to: '/events', label: 'Events', icon: CalendarDays },
      { to: '/event_sessions', label: 'Sub-events', icon: CalendarDays },
      { to: '/rsvps', label: 'RSVPs', icon: CalendarCheck },
      { to: '/meals', label: 'Meal Options', icon: Ticket },
    ],
  },
  {
    group: 'Giving',
    items: [
      { to: '/donations', label: 'Gifts', icon: HandHeart },
      { to: '/campaigns', label: 'Campaigns', icon: Target },
      { to: '/class_standings', label: 'Class Standings', icon: Trophy },
      { to: '/honor_years', label: 'Honor Years', icon: Trophy },
      { to: '/funds', label: 'Designations', icon: ListFilter },
      { to: '/giving_settings', label: 'Giving Settings', icon: Settings },
    ],
  },
  {
    group: 'Content',
    items: [
      { to: '/articles', label: 'News & Articles', icon: FileText },
      {
        to: '/devotionals',
        label: 'Devotionals',
        icon: Sunrise,
        tabs: [
          { to: '/devotionals', label: 'Devotionals' },
          { to: '/devotional_reads', label: 'Reads' },
        ],
      },
      { to: '/releases', label: "What's New", icon: Sparkles },
      {
        to: '/photos',
        label: 'Photos',
        icon: ImageIcon,
        tabs: [
          { to: '/photos', label: 'Gallery' },
          { to: '/albums', label: 'Albums' },
          { to: '/photos/bulk-upload', label: 'Bulk Upload' },
          // The photo-tag NOMINATIONS queue — distinct from System › Maintenance ›
          // Photo Tag Backfill, the one-time notification catch-up.
          { to: '/photo_tags', label: 'Tag Queue' },
        ],
      },
    ],
  },
  {
    group: 'Trust & Safety',
    items: [
      { to: '/reports', label: 'Reports Queue', icon: Flag },
      {
        to: '/feed-moderation',
        label: 'Feed Moderation',
        icon: Newspaper,
        tabs: [
          { to: '/feed-moderation', label: 'Moderate' },
          { to: '/user_posts', label: 'Member Posts' },
          // Comments on Classroom memories (Classrooms Phase 1) — read and delete.
          { to: '/memory_comments', label: 'Memory Comments' },
          { to: '/moderation_actions', label: 'Moderation Log' },
        ],
      },
      { to: '/integrity-actions', label: 'Integrity Actions', icon: ShieldCheck },
      { to: '/integrity_settings', label: 'Safety Controls', icon: ShieldAlert },
      { to: '/text_blocklist', label: 'Text Blocklist', icon: Ban },
    ],
  },
  {
    group: 'Messaging',
    items: [
      {
        to: '/send-push',
        label: 'Push',
        icon: Send,
        tabs: [
          { to: '/send-push', label: 'Send' },
          { to: '/push-broadcasts', label: 'Sent History' },
        ],
      },
      { to: '/notifications', label: 'In-app Notifications', icon: Bell },
      // The Rule Studio: an app event becomes a notification and/or feed post.
      { to: '/notification_rules', label: 'Notification Rules', icon: RefreshCw },
      {
        to: '/agent/inbox',
        label: 'Community Manager',
        icon: UserCheck,
        tabs: [
          { to: '/agent/inbox', label: 'Inbox' },
          { to: '/agent/signals', label: 'Signals' },
          { to: '/agent/runs', label: 'Runs' },
          { to: '/agent/controls', label: 'Controls' },
        ],
      },
    ],
  },
  {
    group: 'System',
    items: [
      {
        // The app-wide levers on the app_config row. Kill Switch also carries
        // the Urgent Notice card.
        to: '/kill-switch',
        label: 'App Controls',
        icon: SlidersHorizontal,
        tabs: [
          { to: '/kill-switch', label: 'Kill Switch & Notices' },
          { to: '/force-update', label: 'App Updates' },
          { to: '/feed-layout', label: 'Feed Layout' },
        ],
      },
      {
        to: '/feature_flags',
        label: 'Feature Flags',
        icon: Settings,
        tabs: [
          { to: '/feature_flags', label: 'All Flags' },
          { to: '/onboarding_settings', label: 'Onboarding' },
          { to: '/demo_settings', label: 'Demo Mode' },
        ],
      },
      { to: '/audit_log', label: 'Audit Trail', icon: History },
      {
        // Destructive or one-off tools, kept together and out of daily paths.
        // Photo Tag Backfill is the one-time catch-up for the photo.tagged rule.
        to: '/purge-test-users',
        label: 'Maintenance',
        icon: Trash2,
        tabs: [
          { to: '/purge-test-users', label: 'Test Data' },
          { to: '/photo-tag-backfill', label: 'Photo Tag Backfill' },
        ],
      },
    ],
  },
];

/** Whether a nav item is active for the current path (exact for '/'). */
export function isActive(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(to + '/');
}

export interface ActiveNav {
  group: AdminNavGroup;
  item: AdminNavItem;
  /** The matched tab, when the item has a tab strip. */
  tab?: AdminNavTab;
  /** The route that matched — the tab's, or the item's own. */
  to: string;
}

/**
 * The ONE sidebar entry (and tab) the current path belongs to. Longest matching
 * route wins, so /photos/bulk-upload resolves to Bulk Upload even though /photos
 * also prefix-matches it. Plain isActive() lit both; the sidebar highlight, the
 * header title and the section strip all read this instead so they always agree.
 */
export function activeNav(pathname: string, nav: AdminNavGroup[] = ADMIN_NAV): ActiveNav | undefined {
  let best: ActiveNav | undefined;
  for (const group of nav) {
    for (const item of group.items) {
      const candidates: { to: string; tab?: AdminNavTab }[] = item.tabs
        ? item.tabs.map((tab) => ({ to: tab.to, tab }))
        : [{ to: item.to }];
      for (const c of candidates) {
        if (isActive(pathname, c.to) && (!best || c.to.length > best.to.length)) {
          best = { group, item, tab: c.tab, to: c.to };
        }
      }
    }
  }
  return best;
}

/** Every label a person can click — sidebar items AND tabs — for uniqueness checks. */
export function allNavLabels(nav: AdminNavGroup[] = ADMIN_NAV): string[] {
  return nav.flatMap((g) =>
    g.items.flatMap((it) => [it.label, ...(it.tabs ?? []).map((t) => t.label).filter((l) => l !== it.label)]),
  );
}
